import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Package, Home, Send, Truck, CreditCard, 
  User, LogOut, Menu, X, ChevronRight, Settings, BarChart3, Car, Search, Route, Shield, Wallet, History
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import NotificationBell from './NotificationBell';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const senderLinks = [
    { path: '/dashboard', icon: Home, label: 'Tableau de bord' },
    { path: '/dashboard/search-trips', icon: Search, label: 'Trouver un trajet' },
    { path: '/dashboard/new-parcel', icon: Send, label: 'Nouvel envoi' },
    { path: '/dashboard/parcels', icon: Package, label: 'Mes envois' },
    { path: '/dashboard/payments', icon: CreditCard, label: 'Paiements' },
    { path: '/dashboard/profile', icon: User, label: 'Profil' },
    { path: '/dashboard/verification', icon: Shield, label: 'Vérification ID' },
  ];

  const carrierLinks = [
    { path: '/dashboard', icon: Home, label: 'Tableau de bord' },
    { path: '/dashboard/new-trip', icon: Car, label: 'Publier un trajet' },
    { path: '/dashboard/my-trips', icon: Route, label: 'Mes trajets' },
    { path: '/dashboard/available', icon: Package, label: 'Colis disponibles' },
    { path: '/dashboard/my-missions', icon: Truck, label: 'Mes livraisons' },
    { path: '/dashboard/earnings', icon: CreditCard, label: 'Mes gains' },
    { path: '/dashboard/history', icon: History, label: 'Historique' },
    { path: '/dashboard/profile', icon: User, label: 'Profil' },
    { path: '/dashboard/verification', icon: Shield, label: 'Vérification ID' },
  ];

  // Liens pour les utilisateurs polyvalents (expéditeur + transporteur)
  const userLinks = [
    { path: '/dashboard', icon: Home, label: 'Tableau de bord' },
    { path: '/dashboard/search-trips', icon: Search, label: 'Trouver un trajet' },
    { path: '/dashboard/new-parcel', icon: Send, label: 'Nouvel envoi' },
    { path: '/dashboard/parcels', icon: Package, label: 'Mes envois' },
    { path: '/dashboard/new-trip', icon: Car, label: 'Publier un trajet' },
    { path: '/dashboard/my-trips', icon: Route, label: 'Mes trajets' },
    { path: '/dashboard/available', icon: Package, label: 'Colis disponibles' },
    { path: '/dashboard/my-missions', icon: Truck, label: 'Mes livraisons' },
    { path: '/dashboard/payments', icon: CreditCard, label: 'Paiements' },
    { path: '/dashboard/earnings', icon: Wallet, label: 'Mes gains' },
    { path: '/dashboard/profile', icon: User, label: 'Profil' },
    { path: '/dashboard/verification', icon: Shield, label: 'Vérification ID' },
  ];

  const adminLinks = [
    { path: '/admin', icon: Home, label: 'Tableau de bord' },
    { path: '/admin/users', icon: User, label: 'Utilisateurs' },
    { path: '/admin/parcels', icon: Package, label: 'Toutes les missions' },
    { path: '/admin/payouts', icon: Wallet, label: 'Versements' },
    { path: '/admin/reports', icon: BarChart3, label: 'Rapports' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
  ];

  const getLinks = () => {
    if (user?.role === 'admin') return adminLinks;
    if (user?.role === 'carrier') return carrierLinks;
    if (user?.role === 'user') return userLinks;
    return senderLinks;
  };

  const links = getLinks();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-2">
              <Package className="w-8 h-8 text-orange-500" />
              <span className="font-['Barlow_Condensed'] text-xl font-bold text-white">COLISFLASH</span>
            </Link>
            <button 
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">{user?.name?.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">{user?.name}</p>
                <p className="text-slate-400 text-xs capitalize">
                  {user?.role === 'sender' ? 'Expéditeur' : 
                   user?.role === 'carrier' ? 'Cotransporteur' : 
                   user?.role === 'user' ? 'Expéditeur & Transporteur' : 'Admin'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {links.map((link, index) => {
              // Section header
              if (link.section) {
                return (
                  <div key={`section-${index}`} className="pt-4 pb-2 first:pt-0">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3">
                      {link.section}
                    </p>
                  </div>
                );
              }
              
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm",
                    isActive 
                      ? "bg-orange-500 text-white" 
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  )}
                  data-testid={`nav-${link.path.replace(/\//g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-all text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-4 ml-auto">
              <NotificationBell />
              <span className="text-sm text-slate-600">
                Bienvenue, <span className="font-semibold text-slate-900">{user?.name}</span>
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
